import { useEffect, useMemo, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Grupos() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [tenants, setTenants] = useState([]);
  const [ramais, setRamais] = useState([]);
  const [mohRows, setMohRows] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/ramais"),
      api.get("/moh"),
      isSuperAdmin ? api.get("/tenants") : api.get("/tenants/current").then(({ data }) => ({ data: data ? [data] : [] })),
    ])
      .then(([extensionsResponse, mohResponse, tenantsResponse]) => {
        setRamais(extensionsResponse.data || []);
        setMohRows(mohResponse.data || []);
        setTenants(tenantsResponse.data || []);
      })
      .catch(() => {
        setRamais([]);
        setMohRows([]);
        setTenants([]);
      });
  }, [isSuperAdmin]);

  const tenantMap = useMemo(
    () => Object.fromEntries(tenants.map((item) => [item.id, item.nome])),
    [tenants],
  );
  const tenantById = useMemo(
    () => Object.fromEntries((tenants || []).map((item) => [item.id, item])),
    [tenants],
  );
  const branchesForTenant = (tenantId) => tenantById[tenantId || user?.tenant_id]?.branches || [];
  const mohOptionsForTenant = (tenantId) => {
    const scopedTenantId = tenantId || user?.tenant_id || "";
    const tenant = tenantById[scopedTenantId] || null;
    const defaultMohUri = tenant?.default_moh_uri || "local_stream://moh";
    const tenantMohRows = (mohRows || [])
      .filter((row) => row?.enabled !== false && (!scopedTenantId || row?.tenant_id === scopedTenantId))
      .map((row) => ({
        value: row.runtime_class ? `local_stream://${row.runtime_class}` : (row.audio_path || row.classe || "local_stream://moh"),
        label: row.nome || row.classe || "Musica em espera",
      }));
    const options = [
      { value: defaultMohUri, label: "Padrao do cliente" },
      ...tenantMohRows,
    ];
    const seen = new Set();
    return options.filter((option) => {
      const key = String(option.value || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  return (
    <CrudPage
      title="Grupos de toque"
      subtitle="Grupo de comunicacao, CID proprio e distribuicao entre ramais"
      endpoint="/grupos"
      testidPrefix="grupos"
      headers={[...(isSuperAdmin ? ["Cliente"] : []), "Numero", "Nome", "Filial", "CID do grupo", "Ramais", "Estrategia", "Status"]}
      defaultValues={{
        tenant_id: user?.tenant_id || "",
        branch_id: "",
        numero: "",
        nome: "",
        caller_id: "",
        max_channels_inbound: 0,
        max_channels_outbound: 0,
        ramais: [],
        estrategia: "simultaneo",
        timeout: 25,
        tocar_musica_espera: false,
        musica_espera: "local_stream://moh",
        fallback: "",
        enabled: true,
      }}
      transformFormOpen={(form) => ({ ...form, ramais: Array.isArray(form.ramais) ? form.ramais : [] })}
      transformSubmit={(form) => ({
        ...form,
        branch_id: String(form.branch_id || "").trim() || null,
        max_channels_inbound: Number(form.max_channels_inbound || 0),
        max_channels_outbound: Number(form.max_channels_outbound || 0),
        ramais: Array.isArray(form.ramais) ? form.ramais : [],
      })}
      renderRow={(row) => (
        <>
          {isSuperAdmin && <td>{tenantMap[row.tenant_id] || row.tenant_id || "-"}</td>}
          <td className="font-mono">{row.numero}</td>
          <td>{row.nome}</td>
          <td>{row.branch_name || "Escopo geral"}</td>
          <td className="font-mono text-xs">{row.caller_id || "-"}</td>
          <td className="font-mono text-xs">{(row.ramais || []).join(", ") || "-"}</td>
          <td>{row.estrategia === "simultaneo" ? "Simultaneo" : "Sequencial"}</td>
          <td>{row.enabled ? <span className="sbc-tag tag-online">ativo</span> : <span className="sbc-tag tag-offline">inativo</span>}</td>
        </>
      )}
      fields={[
        ...(isSuperAdmin
          ? [
              {
                key: "tenant_id",
                label: "Cliente do grupo",
                type: "select",
                full: true,
                options: [
                  { value: "", label: "-- selecione o cliente --" },
                  ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.nome })),
                ],
                onChange: (next) => ({ ...next, branch_id: "", ramais: [] }),
              },
            ]
          : []),
        {
          key: "branch_id",
          label: "Unidade / filial",
          type: "select",
          full: true,
          options: (form) => [
            { value: "", label: "Escopo geral do cliente" },
            ...branchesForTenant(form.tenant_id).filter((branch) => branch.enabled !== false).map((branch) => ({
              value: branch.id,
              label: branch.nome || branch.codigo || branch.id,
            })),
          ],
          onChange: (next) => ({ ...next, ramais: [] }),
          hint: "Os membros do grupo serao limitados aos ramais da filial selecionada.",
        },
        { key: "numero", label: "Numero do grupo", mono: true, placeholder: "600" },
        { key: "nome", label: "Nome do grupo", placeholder: "Ex: Comercial geral" },
        {
          key: "caller_id",
          label: "CID do grupo de comunicacao",
          mono: true,
          full: true,
          placeholder: "+557135999160",
          hint: "Quando preenchido, chamadas enviadas por este grupo para a operadora usam este CID.",
        },
        { key: "max_channels_inbound", label: "Limite de canais de entrada", type: "number", hint: "Zero usa o limite do cliente/filial." },
        { key: "max_channels_outbound", label: "Limite de canais de saida", type: "number", hint: "Zero usa o limite do cliente/filial." },
        {
          key: "tocar_musica_espera",
          label: "Tocar musica em espera enquanto o grupo chama",
          type: "toggle",
          full: true,
        },
        {
          key: "musica_espera",
          label: "Musica em espera do grupo",
          type: "select",
          full: true,
          options: (form) => mohOptionsForTenant(form.tenant_id),
          hint: "A pessoa que ligar para o grupo ouvira esta musica ate um dos ramais atender.",
          hidden: (form) => !form.tocar_musica_espera,
        },
        {
          key: "ramais",
          label: "Ramais do grupo",
          type: "checkbox-list",
          full: true,
          options: (form) =>
            ramais
              .filter((ramal) => ramal.enabled !== false
                && (!form.tenant_id || ramal.tenant_id === form.tenant_id)
                && (!form.branch_id || String(ramal.branch_id || "") === String(form.branch_id)))
              .map((ramal) => ({
                value: ramal.numero,
                label: `${ramal.numero} - ${ramal.nome || "Sem nome"}`,
                hint: ramal.caller_id || "Ramal interno",
              })),
          hint: "Ao escolher o cliente, a lista mostra somente os ramais dele.",
        },
        {
          key: "estrategia",
          label: "Estrategia",
          type: "select",
          options: [
            { value: "simultaneo", label: "Simultaneo" },
            { value: "sequencial", label: "Sequencial" },
          ],
        },
        { key: "timeout", label: "Tempo de toque", type: "number" },
        { key: "fallback", label: "Destino alternativo", mono: true, placeholder: "1000" },
        { key: "enabled", label: "Ativo", type: "toggle" },
      ]}
    />
  );
}
